import {
  type LucideIcon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CornerDownRight,
  Flag,
  Handshake,
  Inbox,
  Mail,
  MessageSquare,
  ScanLine,
  Send,
  Upload,
  User,
  XCircle,
} from 'lucide-react';
import { useInvoiceAudit } from '../../api/audit.js';
import type { InvoiceAuditEntry } from '../../api/types.js';
import { formatInvoiceDate, formatRelative } from './format.js';
import { EmptyState } from '../../components/EmptyState.js';
import { describeOutcome, type DisputeOutcome } from './disputeOutcome.js';

interface Props {
  primaryKey: string;
  invoiceCreatedAt: string | null;
  // REQ-FE-032 area B14 — cap the rail to ~5 rows with a "Voir tout
  // l'historique →" link. The standalone detail screen still uses the
  // full list (cap=null). Centre-pane mode passes 5.
  cap?: number | null;
  // When true, render a "Voir tout l'historique →" link beneath the
  // capped list. Caller-supplied since the modal/drawer is owned by
  // the parent (centre-pane vs standalone). Defaults off.
  onShowAll?: (() => void) | null;
}

// REQ-FE-032 area B11 — `invoice.read` events are pure FE telemetry; they
// pollute the user-facing rail. Filter them out before rendering.
const NOISE_ACTIONS = new Set<string>(['invoice.read']);

interface ActionMeta {
  // REQ-FE-030: Lucide line icon (was emoji). Rendered at size=14
  // strokeWidth=1.5 in the row gutter.
  Icon: LucideIcon;
  label: (e: InvoiceAuditEntry) => string;
}

function placeholder(meta: Record<string, unknown> | null | undefined, key: string): string | null {
  if (!meta) return null;
  const v = meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

const ACTIONS: Record<string, ActionMeta> = {
  'invoice.received': { Icon: Inbox, label: () => "Facture reçue d'Iopole" },
  'invoice.uploaded': { Icon: Upload, label: () => 'Facture importée manuellement' },
  'invoice.ocr.parsed': { Icon: ScanLine, label: () => 'OCR — champs extraits' },
  'invoice.assigned': {
    Icon: User,
    label: (e) => {
      const a = placeholder(e.metadata ?? null, 'assignee');
      return a ? `Assignée à ${a}` : 'Assignée';
    },
  },
  'invoice.reassigned': {
    Icon: User,
    label: (e) => {
      const a = placeholder(e.metadata ?? null, 'assignee');
      return a ? `Réassignée à ${a}` : 'Réassignée';
    },
  },
  'invoice.dispute.raised': {
    Icon: Flag,
    label: (e) => {
      const m = placeholder(e.metadata ?? null, 'motif');
      return m ? `Litige ouvert — ${m}` : 'Litige ouvert';
    },
  },
  'invoice.dispute.withdrawn': { Icon: Flag, label: () => 'Litige retiré' },
  'invoice.dispute.resolved': {
    Icon: Handshake,
    label: (e) => {
      const o = placeholder(e.metadata ?? null, 'outcome');
      if (!o) return 'Litige résolu';
      const p = describeOutcome(o as DisputeOutcome);
      return p.labelFr;
    },
  },
  'invoice.validation.requested': {
    Icon: Mail,
    label: (e) => {
      const v = placeholder(e.metadata ?? null, 'validator');
      return v ? `Validation demandée à ${v}` : 'Validation demandée';
    },
  },
  'invoice.validation.accepted': { Icon: CheckCircle2, label: () => 'Validation acceptée' },
  'invoice.validation.refused': { Icon: XCircle, label: () => 'Validation refusée' },
  'invoice.send.requested': { Icon: Send, label: () => 'Envoi vers Iopole demandé' },
  'invoice.send.confirmed': { Icon: CheckCircle2, label: () => 'Envoi confirmé' },
  'invoice.send.failed': { Icon: AlertTriangle, label: () => "Échec d'envoi" },
  'invoice.state.changed': {
    Icon: CornerDownRight,
    label: (e) => {
      const from = placeholder(e.metadata ?? null, 'from');
      const to = placeholder(e.metadata ?? null, 'to');
      return from && to ? `État: ${from} → ${to}` : 'État modifié';
    },
  },
  'comment.added': { Icon: MessageSquare, label: () => 'Commentaire ajouté' },
  'comment.deleted': { Icon: MessageSquare, label: () => 'Commentaire supprimé' },
};

function actorName(e: InvoiceAuditEntry): string {
  if (e.actorUid === null) return 'Système';
  return e.actorDisplayName ?? e.actorEmail ?? e.actorUid;
}

function isIdempotentSkip(reason: string | null | undefined): boolean {
  return typeof reason === 'string' && /^idempotent_skip/.test(reason);
}

export function ActivityTimeline({
  primaryKey,
  invoiceCreatedAt,
  cap = null,
  onShowAll = null,
}: Props) {
  const audit = useInvoiceAudit(primaryKey);

  if (audit.isLoading) {
    return (
      <div className="activity-timeline" data-testid="activity-loading">
        <div className="muted" style={{ fontSize: 12 }}>
          Chargement de l&apos;historique…
        </div>
      </div>
    );
  }
  if (audit.isError) {
    return (
      <div className="activity-timeline" data-testid="activity-error" role="alert">
        Impossible de charger l&apos;historique.
      </div>
    );
  }

  // REQ-FE-032 area B11 — drop noise actions (invoice.read) before
  // rendering. The unfiltered total stays around so the "Voir tout"
  // link can disclose the count.
  const rawRows = audit.data?.data ?? [];
  const businessRows = rawRows.filter((e) => !NOISE_ACTIONS.has(e.action));
  // REQ-FE-032 area B14 — cap to the most-recent N when caller asks.
  const visibleRows = cap != null && cap > 0 ? businessRows.slice(0, cap) : businessRows;
  const hiddenCount = businessRows.length - visibleRows.length;
  const rows = visibleRows;
  if (businessRows.length === 0) {
    return (
      <div className="activity-timeline" data-testid="activity-empty">
        <EmptyState
          variant="inline"
          icon={<Clock aria-hidden="true" size={18} strokeWidth={1.5} />}
          title="Aucune activité enregistrée"
          description="L'historique apparaîtra dès la première action."
        />
      </div>
    );
  }

  // Oldest row (rows are newest-first per BE). Read from the full
  // (un-capped) business set so the mirror-disclosure stays accurate
  // even when the rail is showing only the most-recent ~5.
  const oldest = businessRows[businessRows.length - 1]!;
  const oldestTs = new Date(oldest.ts).getTime();
  const createdAtTs = invoiceCreatedAt ? new Date(invoiceCreatedAt).getTime() : NaN;
  const showMirrorDisclosure =
    !Number.isNaN(createdAtTs) && !Number.isNaN(oldestTs) && oldestTs > createdAtTs;

  return (
    <div className="activity-timeline" data-testid="activity-timeline">
      <ol className="activity-timeline__list">
        {rows.map((e) => {
          const known = ACTIONS[e.action];
          const isUnknown = !known;
          const Icon = known?.Icon;
          const label = known ? known.label(e) : null;
          const failure = e.outcome === 'failure';
          const skipped = isIdempotentSkip(e.reason);
          return (
            <li key={e.auditId} className="activity-row" data-testid={`activity-row-${e.auditId}`}>
              <span className="activity-row__icon" aria-hidden="true">
                {Icon ? <Icon size={14} strokeWidth={1.5} /> : <span>•</span>}
              </span>
              <div className="activity-row__main">
                <div className="activity-row__head">
                  {isUnknown ? (
                    <code
                      className="activity-row__raw"
                      data-testid={`activity-row-${e.auditId}-unknown`}
                    >
                      {e.action}
                    </code>
                  ) : (
                    <span className="activity-row__label">{label}</span>
                  )}
                  {failure ? (
                    <span
                      className="pill pill--danger activity-row__chip"
                      data-testid={`activity-row-${e.auditId}-failure`}
                    >
                      échec
                    </span>
                  ) : skipped ? (
                    <span className="pill pill--neutral activity-row__chip">ignoré</span>
                  ) : null}
                </div>
                <div className="muted activity-row__meta">
                  <span>{actorName(e)}</span>
                  <span> · </span>
                  <span title={e.ts}>{formatRelative(e.ts)}</span>
                </div>
                {e.reason ? (
                  <div
                    className="activity-row__reason"
                    data-testid={`activity-row-${e.auditId}-reason`}
                  >
                    <em>{e.reason}</em>
                  </div>
                ) : null}
                {isUnknown && e.metadata ? (
                  <details className="activity-row__details">
                    <summary>Détails</summary>
                    <pre>{JSON.stringify(e.metadata, null, 2)}</pre>
                  </details>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
      {hiddenCount > 0 && onShowAll ? (
        <button
          type="button"
          className="activity-timeline__show-all"
          onClick={onShowAll}
          data-testid="activity-show-all"
        >
          Voir tout l&apos;historique ({hiddenCount}+) →
        </button>
      ) : hiddenCount > 0 ? (
        <div className="muted activity-timeline__disclosure" data-testid="activity-hidden-count">
          {hiddenCount} action{hiddenCount > 1 ? 's' : ''} antérieure{hiddenCount > 1 ? 's' : ''}.
        </div>
      ) : null}
      {showMirrorDisclosure ? (
        <div
          className="muted activity-timeline__disclosure"
          data-testid="activity-mirror-disclosure"
        >
          Historique disponible depuis le {formatInvoiceDate(oldest.ts.slice(0, 10))}
        </div>
      ) : null}
    </div>
  );
}
